
// Libaries
import { Component, namespace, Vue } from "nuxt-property-decorator";

import {
  formatTopCitiesTableData,
  getTableHeaderData,
  replaceToUmlauts,
  returnPriceFormatDE,
} from "~/mixins/formatting";

// Components
import FederalStates from "~/assets/data/federalStates";

// Mixins
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import DistrictLocationsList from "~/components/molecules/display/DistrictLocationsList.vue";
import DistrictOtherLocations from "~/components/molecules/display/DistrictOtherLocations.vue";
import PelletsChartComparison from "~/components/organisms/chart/PelletsChartComparison.vue";
import CityListSection from "~/components/organisms/display/geographySites/CityListSection.vue";
import DualVerticalDataTile from "~/components/organisms/display/geographySites/DualVerticalDataTile.vue";
import PelletsDataDisplay from "~/components/organisms/display/geographySites/PelletsDataDisplay.vue";
import OilImportTable from "~/components/organisms/table/OilImportTable.vue";

import {
  createBreadCrumbsStructuredData,
  formatBreadCrumbByCountyAndBundesland,
} from "~/mixins/breadcrumb";
import {
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/translations";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { STANDARD_PELLETS_QUANTITY } from "~/types";
import { PartnerType } from "~/types/Partner";
import { isCityState } from "~/utils/geographyUtils";
import { calculatePelletsChartMetrics } from "~/utils/priceUtils";

const BreadCrumbModule = namespace("breadcrumb");
const FederalStatesModule = namespace("federalstates");

@Component({
  scrollToTop: true,
  components: {
    BreadCrumb,
    DistrictLocationsList,
    OilImportTable,
    DistrictOtherLocations,
    PelletsDataDisplay,
    DualVerticalDataTile,
    CityListSection,
    PelletsChartComparison,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
    replaceToUmlauts,
    getTableHeaderData,
    formatTopCitiesTableData,
  },
})
export default class FederalStateView extends Vue {
  head() {
    return {
      ...generateMetaTags(this.$route.path, "federal-states-pellets", [
        { k: "category", v: translateStateName(this.$route.params.category) },
      ]),
      link: [
        {
          rel: "canonical",
          href: `/pelletspreise/bundeslaender/${this.$route.params.category.toLowerCase()}`,
        },
      ],
      script: [
        {
          json: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(
              this.breadCrumb,
              "pellets",
            ),
          },
          type: "application/ld+json",
        },
      ],
    };
  }
  translateStateName = translateStateName;
  replaceToUmlauts = replaceToUmlauts;
  getTableHeaderData = getTableHeaderData;
  formatTopCitiesTableData = formatTopCitiesTableData;

  STANDARD_PELLETS_QUANTITY = STANDARD_PELLETS_QUANTITY;

  @BreadCrumbModule.Mutation("setBreadCrumbForBundeslandAndCounty")
  setBreadCrumbForBundeslandAndCounty;

  @BreadCrumbModule.State("breadCrumb")
  breadCrumb;

  @FederalStatesModule.State("bigCityLinks")
  bigCityLinks;

  @FederalStatesModule.State("districtCities")
  districtCities;

  @FederalStatesModule.State("pelletsStateArticle")
  pelletsStateArticle;

  @FederalStatesModule.State("pelletsStateAssetsPrice")
  pelletsStateAssetsPrice;

  @FederalStatesModule.State("pelletsTopCities")
  pelletsTopCities;

  @FederalStatesModule.State("pelletsChartData")
  pelletsChartData;

  async fetch() {
    try {
      this.validateState();
      await this.fetchStateData();
      this.updateBreadcrumbs();
    } catch (error) {
      this.handleError(error);
    }
  }

  validateState() {
    const isValidState = FederalStates.find(
      (state) =>
        state.title.toLocaleLowerCase() ===
        replaceToUmlauts(this.$route.params.category),
    );

    if (!isValidState) {
      throw {
        statusCode: 404,
        message: "Dieses Bundesland wurde nicht gefunden",
      };
    }
  }

  async fetchStateData(): Promise<void> {
    const category = replaceToUmlauts(this.$route.params.category);
    const nutsCode = translateStateToNutsCode(category);
    const stateName = translateStateName(this.$route.params.category);

    await Promise.all([
      this.fetchCounties(nutsCode, "PARTNER_PELLET"),
      this.fetchDistrictCities(nutsCode, "PARTNER_PELLET"),
      this.fetchPelletsTopStateCities(stateName),
      this.fetchCurrentPelletsStatePrice(nutsCode),
      this.fetchCities(stateName, "PARTNER_PELLET"),
      this.fetchPelletsStateChartData(nutsCode),
      this.fetchPelletsStateArticle(stateName),
    ]);
  }

  updateBreadcrumbs() {
    const crumbs = formatBreadCrumbByCountyAndBundesland(
      this.$route.fullPath,
      this.$router,
    );

    this.setBreadCrumbForBundeslandAndCounty({
      breadCrumbData: crumbs,
      fullPath: this.$route.fullPath,
    });
  }

  handleError(error) {
    if (error.statusCode) {
      this.$nuxt.error(error);
    } else {
      console.error("Error fetching data:", error);
      this.$nuxt.error({
        statusCode: 500,
        message: "Ein unerwarteter Fehler ist aufgetreten",
      });
    }
  }

  async fetchDistrictCities(
    stateNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchDistrictCities", {
      stateNutsCode,
      includeLinkField: true,
      partnerType,
    });
  }

  async fetchPelletsCountyDistrictChartData(countyNuts: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsCountyDistrictChartData",
      {
        countyNutsCode: countyNuts,
      },
    );
  }

  async fetchPelletsStateChartData(nutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchPelletsStateChartData", {
      nutsCode,
    });
  }

  async fetchPelletsStateArticle(stateName: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsStateArticle",
      stateName,
    );
  }

  async fetchCities(stateName: string, partnerType: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchBigCityLinks", {
      stateName,
      partnerType,
      urlPrefix: "pelletspreise",
    });
  }

  async fetchCounties(
    statesNutsCode: string,
    partnerType?: PartnerType,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyLinks", {
      statesNutsCode,
      partnerType,
    });
  }

  async fetchCurrentPelletsStatePrice(nutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCurrentPelletsStatePrice", {
      nutsCode,
    });
  }

  async fetchPelletsTopStateCities(stateName: string): Promise<void> {
    await this.$store.dispatch(
      "federalstates/fetchPelletsTopStateCities",
      stateName,
    );
  }

  get pelletsMetrics() {
    return calculatePelletsChartMetrics(this.pelletsChartData);
  }

  get returnPriceFormatDE() {
    return returnPriceFormatDE;
  }

  get stateName(): string {
    return translateStateName(this.$route.params.category);
  }

  get isCityStateValue(): boolean {
    return isCityState(this.$route.params.category);
  }
}
